/*
 * Port attribution:
 *   - Shoelace <sl-badge>      MIT — Copyright (c) 2020 A Beautiful Site, LLC
 *     https://github.com/shoelace-style/shoelace/blob/next/src/components/badge/badge.component.ts
 *   - daisyUI .badge           MIT — Copyright (c) Pouya Saadeghi
 *     https://github.com/saadeghi/daisyui/blob/master/packages/daisyui/src/components/badge.css
 *
 * Surface contract (variant / pill / pulse) is faithful to Shoelace.
 * Expanded palette (secondary, accent, info, neutral) and outline / ghost
 * modifiers + xs/sm/md/lg/xl sizes are lifted from daisyUI to give the
 * single component the union of both libraries' surfaces.
 *
 * Combined visual reference lives in /demos/parity/badge.html.
 */

/**
 * @component ry-badge
 * @category  display
 * @summary   Inline status / count indicator. Pure visual by default. Use `pulse` to draw attention to live updates and `role="status"` semantics when the badge mirrors a changing value (notification count, presence dot).
 *
 * Shoelace's `<sl-badge>` is the surface contract for `variant` / `pill` /
 * `pulse`. daisyUI's `.badge` is the visual contract for the wider palette
 * (`secondary`, `accent`, `info`, `neutral`), `outline`, `ghost`, and the
 * `xs`/`sm`/`md`/`lg`/`xl` size scale.
 *
 * @attribute variant   'primary'|'secondary'|'accent'|'info'|'success'|'warning'|'danger'|'neutral'   default='primary'   Color theme.
 * @attribute size      'xs'|'sm'|'md'|'lg'|'xl'   default='md'   Compact → enlarged scale.
 * @attribute pill      boolean   default=false   Fully rounded ends. (Default badge already has a soft radius; `pill` is the capsule shape.)
 * @attribute pulse     boolean   default=false   Animates a halo behind the badge so the eye catches updates. Respects `prefers-reduced-motion`.
 * @attribute outline   boolean   default=false   Transparent fill, colored border + text. Mutually exclusive with `ghost`.
 * @attribute ghost     boolean   default=false   Transparent fill, colored text, no border. Mutually exclusive with `outline`.
 *
 * @content  The badge's default slot. Typically a string (count, label) or a tiny inline icon. Keep it short — badges are visual punctuation, not text containers.
 *
 * @dom
 *   ry-badge[variant][size][pill?][pulse?][outline?][ghost?]
 *     > {slotted content}
 *
 *   No internal wrapper. The host element IS the badge — visual styling and
 *   layout target the custom element directly. This keeps DOM weight minimal
 *   and makes badge composable inside flow text without an extra inline-block
 *   level.
 *
 * @gotcha  Badges are inert. ry-badge does NOT bind a click handler, focus
 *   ring, or any keyboard behavior — wrap one in `<ry-button>` if you need
 *   an action. The component sets `role="status"` on connect so screen
 *   readers announce changes; if the badge is purely decorative (label
 *   suffix on a card), pass `role="presentation"` to silence it.
 *
 * @gotcha  `outline` and `ghost` are read live (no setup-once snapshot),
 *   so toggling them at runtime updates the look. Other attributes are
 *   styled through CSS attribute selectors, so changing `variant` / `size` /
 *   `pill` / `pulse` at runtime is equally fine.
 *
 * @example default
 * ```html
 * <ry-badge>NEW</ry-badge>
 * ```
 *
 * @example variant-primary
 * ```html
 * <ry-badge variant="primary">Primary</ry-badge>
 * ```
 *
 * @example variant-secondary
 * ```html
 * <ry-badge variant="secondary">Secondary</ry-badge>
 * ```
 *
 * @example variant-accent
 * ```html
 * <ry-badge variant="accent">Accent</ry-badge>
 * ```
 *
 * @example variant-info
 * ```html
 * <ry-badge variant="info">Info</ry-badge>
 * ```
 *
 * @example variant-success
 * ```html
 * <ry-badge variant="success">Success</ry-badge>
 * ```
 *
 * @example variant-warning
 * ```html
 * <ry-badge variant="warning">Warning</ry-badge>
 * ```
 *
 * @example variant-danger
 * ```html
 * <ry-badge variant="danger">Danger</ry-badge>
 * ```
 *
 * @example variant-neutral
 * ```html
 * <ry-badge variant="neutral">Neutral</ry-badge>
 * ```
 *
 * @example size-xs
 * ```html
 * <ry-badge size="xs">xs</ry-badge>
 * ```
 *
 * @example size-sm
 * ```html
 * <ry-badge size="sm">sm</ry-badge>
 * ```
 *
 * @example size-md
 * ```html
 * <ry-badge size="md">md</ry-badge>
 * ```
 *
 * @example size-lg
 * ```html
 * <ry-badge size="lg">lg</ry-badge>
 * ```
 *
 * @example size-xl
 * ```html
 * <ry-badge size="xl">xl</ry-badge>
 * ```
 *
 * @example pill
 * ```html
 * <ry-badge pill variant="primary">99+</ry-badge>
 * ```
 *
 * @example pulse
 * ```html
 * <ry-badge pulse variant="danger">LIVE</ry-badge>
 * ```
 *
 * @example outline
 * ```html
 * <ry-badge outline variant="primary">Outline</ry-badge>
 * ```
 *
 * @example ghost
 * ```html
 * <ry-badge ghost variant="primary">Ghost</ry-badge>
 * ```
 *
 * @tutorial defaults-to-primary-md
 * ```js
 * // Wrong assumption: an unspecified badge is "neutral". Shoelace defaults
 * // variant to "primary" and ry-badge keeps that contract — only the size
 * // ("md") is implicit.
 * const el = mount('<ry-badge>NEW</ry-badge>');
 * await expectEqual(el.getAttribute('role'), 'status', 'role=status set on connect');
 * await expectEqual(el.hasAttribute('variant'), false, 'variant left unset for CSS default');
 * await expectEqual(el.hasAttribute('size'), false, 'size left unset for CSS default');
 * ```
 *
 * @tutorial role-status-can-be-overridden
 * ```js
 * // Wrong assumption: the component clobbers role on every connect. It only
 * // sets role="status" when the consumer hasn't provided one — a badge used
 * // as decoration (e.g. inside a button label) can pass role="presentation".
 * const el = mount('<ry-badge role="presentation">NEW</ry-badge>');
 * await expectEqual(el.getAttribute('role'), 'presentation', 'consumer role wins');
 * ```
 *
 * @tutorial variant-attribute-reflects
 * ```js
 * const el = mount('<ry-badge variant="success">OK</ry-badge>');
 * await expectAttr(el, 'variant', 'success');
 * ```
 *
 * @tutorial pill-pulse-outline-ghost-as-flags
 * ```js
 * // Each modifier is a boolean attribute — its mere presence is the on
 * // state. Setting an empty string keeps the attribute and counts as on.
 * const el = mount('<ry-badge pill pulse outline>X</ry-badge>');
 * await expectEqual(el.hasAttribute('pill'), true);
 * await expectEqual(el.hasAttribute('pulse'), true);
 * await expectEqual(el.hasAttribute('outline'), true);
 * await expectEqual(el.hasAttribute('ghost'), false, 'ghost not set');
 * ```
 *
 * @see ry-tag
 * @see ry-button
 */

import { RyElement } from '../core/ry-element.js';

export class RyBadge extends RyElement {
  setup(): void {
    // role="status" gives a live-region semantic so AT announces count
    // changes. Don't clobber a consumer-supplied role (some badges are
    // purely decorative — they should be role="presentation").
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'status');
    }
  }
}

customElements.define('ry-badge', RyBadge);
